<template>
  <manual-page
    title="Gestión de Mascotas"
    description="Administra las mascotas registradas, sus dietas y chequeos nutricionales."
    :show-video="true"
    :related-links="relatedLinks"
    :action-route="{ name: 'petsIndex' }"
    action-text="Ver mascotas"
  >
    <!-- Overview -->
    <h2>Vista General de Mascotas</h2>
    <p>
      En esta sección encuentras todas las mascotas registradas en la plataforma.
      Cada mascota está asociada a un cliente y puede tener una o más dietas asignadas.
    </p>

    <div class="info-box">
      <p class="font-medium text-gray-700 mb-2">Información disponible de cada mascota:</p>
      <ul class="list-disc list-inside text-gray-600 space-y-1">
        <li>Datos básicos (nombre, especie, raza, edad)</li>
        <li>Peso actual e historial de peso</li>
        <li>Condiciones de salud y alergias</li>
        <li>Dieta actual y composición</li>
        <li>Historial de chequeos nutricionales</li>
        <li>Fotos de la mascota</li>
      </ul>
    </div>

    <!-- Pet List -->
    <h2>Lista de Mascotas</h2>
    <p>La lista muestra información resumida de cada mascota:</p>
    <ul>
      <li><strong>Foto:</strong> Imagen de perfil de la mascota</li>
      <li><strong>Nombre:</strong> Nombre de la mascota</li>
      <li><strong>Especie/Raza:</strong> Tipo de mascota y su raza</li>
      <li><strong>Dueño:</strong> Cliente propietario</li>
      <li><strong>Sucursal:</strong> Franquicia asignada</li>
      <li><strong>Estado:</strong> Si tiene dieta activa</li>
    </ul>

    <div class="tip-box">
      <p class="text-sm text-blue-700">
        <strong>Tip:</strong> Puedes filtrar mascotas por especie (perro/gato),
        por sucursal, o por estado de la dieta.
      </p>
    </div>

    <!-- Pet Detail -->
    <h2>Detalle de la Mascota</h2>
    <p>Al abrir el detalle de una mascota, encontrarás:</p>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">1</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Información Básica</h3>
          <p class="text-gray-600">
            Nombre, especie, raza, género, fecha de nacimiento, estado reproductivo
            (esterilizado/entero), y nivel de actividad física.
          </p>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">2</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Datos de Salud</h3>
          <p class="text-gray-600">
            Peso actual, peso ideal, condición corporal, alergias alimentarias,
            condiciones médicas y medicamentos que toma.
          </p>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">3</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Dieta Actual</h3>
          <p class="text-gray-600">
            Si la mascota tiene una dieta activa, verás la composición detallada:
            proteínas, vegetales, carbohidratos, y las cantidades por comida.
          </p>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">4</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Historial de Chequeos</h3>
          <p class="text-gray-600">
            Registro de chequeos nutricionales realizados. Cada chequeo incluye
            peso, condición corporal, y notas del evaluador.
          </p>
        </div>
      </div>
    </div>

    <!-- Creating Checkups -->
    <h2>Registrar Chequeo Nutricional</h2>
    <p>
      Los chequeos permiten dar seguimiento al progreso de la mascota:
    </p>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">1</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Datos del Chequeo</h3>
          <ul class="list-disc list-inside text-gray-600 space-y-1">
            <li><strong>Peso actual:</strong> Peso medido en el chequeo</li>
            <li><strong>Condición corporal:</strong> Escala 1-9 según estándar veterinario</li>
            <li><strong>Notas:</strong> Observaciones adicionales</li>
          </ul>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">2</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Ajuste de Dieta</h3>
          <p class="text-gray-600">
            Si el chequeo indica que la mascota necesita ajustar su alimentación
            (subir o bajar de peso), puedes recomendar cambios en la dieta.
          </p>
        </div>
      </div>
    </div>

    <div class="tip-box">
      <p class="text-sm text-blue-700">
        <strong>Tip:</strong> Se recomienda hacer chequeos cada 4-6 semanas para
        mascotas en proceso de ajuste de peso, y cada 2-3 meses para mascotas en mantenimiento.
      </p>
    </div>

    <!-- Breeds -->
    <h2>Catálogo de Razas</h2>
    <p>
      El sistema incluye un catálogo de razas de perros y gatos que puedes administrar:
    </p>
    <ul>
      <li>Ver todas las razas registradas</li>
      <li>Agregar nuevas razas</li>
      <li>Clasificar por especie (perro/gato)</li>
      <li>Definir características típicas</li>
    </ul>
    <p>
      Las razas se usan para personalizar recomendaciones de alimentación según
      las características típicas de cada raza.
    </p>

    <!-- Editing Pets -->
    <h2>Editar Información de Mascota</h2>
    <p>Puedes actualizar la información de una mascota cuando sea necesario:</p>
    <ul>
      <li><strong>Peso:</strong> Actualiza cuando el cliente reporte un nuevo peso</li>
      <li><strong>Condiciones médicas:</strong> Si desarrolla nuevas condiciones</li>
      <li><strong>Alergias:</strong> Si se descubren nuevas intolerancias</li>
      <li><strong>Fotos:</strong> Para mantener el registro actualizado</li>
    </ul>

    <div class="warning-box">
      <p class="text-sm text-yellow-800">
        <strong>Importante:</strong> Los cambios en peso o condiciones de salud pueden
        requerir ajustes en la dieta. Considera crear una nueva cotización si los cambios
        son significativos.
      </p>
    </div>

    <!-- Support -->
    <h2>¿Necesitas ayuda?</h2>
    <p>Si tienes dudas sobre la gestión de mascotas:</p>
    <ul>
      <li><strong>WhatsApp:</strong> +52 811 669 1329</li>
      <li><strong>Correo:</strong> soporte@trunimal.com</li>
    </ul>
  </manual-page>
</template>

<script>
import ManualPage from "@/components/manual/ManualPage.vue";

export default {
  name: "ManualPets",

  components: {
    ManualPage,
  },

  data() {
    return {
      relatedLinks: [
        { route: "manualClients", label: "Clientes" },
        { route: "manualQuoteRequests", label: "Solicitudes" },
        { route: "manualPlans", label: "Planes" },
        { route: "manualDietManagement", label: "Alimentos" },
      ],
    };
  },
};
</script>
